import { Coord, GridConfig } from "../../../../../../../../src/types/Interface";
import GridArea from "../../../../../../../../src/entities/GridArea";
import Angle from "../../../../../../../../src/entities/Angle/Angle";
import Line from "../../../../../../../../src/entities/Line/Line";
import getAnimatedEndCoord from "./helpers/getAnimatedEndCoord";
import getExtendedEndCoord from "./helpers/getExtendedEndCoord";
import getAngleBetweenPoints from "../../../../../../../../src/utils/math/getAngleBtwPoints";
import { generateAngles } from "../../../../../../../../src/utils/generator/generateAngles";
import { Question } from "../../../../types/Interface";
import { addCustomDelay, drawLineAnim, drawPointsAnim, movePointAnim } from "../../../../../../../../src/animation/Circle/helpers/drawHelpers";
import { rootEventBus, EnumRootEventBus } from "../../../../../../../../src/managers/RootEventBus";
import getCenterOfTwoCoords from "../../../../../../../../src/utils/coord/getCenterOfTwoCoords";
import PivotPoint from "../../../../entities/PivotPoint";


export default class HintFactory {
  static async drawLinesType1(gridConfig: GridConfig, Question: Question, elements: (Angle | Line)[][]): Promise<void> {
    const { pivotPoint, shapePoints, rotation } = Question;
    const sCoords = shapePoints.map(c => GridArea.getGlobalCoord(gridConfig, c));
    const pivotCoord = GridArea.getGlobalCoord(gridConfig, pivotPoint);

    for (let index = 0; index < sCoords.length; index++) {
      const sCoord = sCoords[index];
      await this.drawLineType1(elements[index], sCoord, pivotCoord, rotation);
    }
    return await new Promise(res => res());
  }

  static async drawLineType1(elements: (Angle | Line)[], sCoord: Coord, pivotCoord: Coord, rotation: number): Promise<void> {
    const lines = [elements[0], elements[1]];
    const angle = elements[2];
    Object.assign(angle, pivotCoord);
    lines.push(angle);

    await drawLineAnim(lines[0] as Line, sCoord, pivotCoord)
    await this.animateLineType1(lines[1] as Line, lines[2] as Angle, sCoord, pivotCoord, rotation)
    return new Promise(res => res())
  }

  static async animateLineType1(line: Line, angle: Angle, sCoord: Coord, pivotCoord: Coord, rotation: number) {
    const startRadian = getAngleBetweenPoints(pivotCoord, sCoord);

    const onUpdate = (radian: number) => {
      const endCoord = getAnimatedEndCoord(sCoord, pivotCoord, radian);
      const deg = generateAngles({
        curr: pivotCoord,
        prev: rotation > 0 ? sCoord : endCoord,
        next: rotation > 0 ? endCoord : sCoord
      }, false).degree
      line.draw(pivotCoord, endCoord);
      angle.draw(pivotCoord, startRadian, radian, deg, rotation > 0 ? false : true);
    }
    const onComplete = async () => {
      await addCustomDelay(450)
      angle.setVisible(false);
    }
    await movePointAnim(startRadian, startRadian + rotation, onUpdate, onComplete);
  }
  static async drawLinesType2(lines: Line[][], indexes: number[], pivPoint: PivotPoint, gridConfig: GridConfig, { shapePoints, rotatedShapePoints, pivotPoint, rotation }: Question) {
    const sCoords = [...shapePoints].map(c => GridArea.getGlobalCoord(gridConfig, c));
    const rSCoords = [...rotatedShapePoints].map(c => GridArea.getGlobalCoord(gridConfig, c));
    const pivotCoord = GridArea.getGlobalCoord(gridConfig, pivotPoint);

    pivPoint.visible = false;
    pivPoint.draw(pivotCoord);

    for (let cI = 0; cI < indexes.length; cI++) {
      const _lines = lines[cI];
      const sCoord = sCoords[indexes[cI]];
      const rSCoord = rSCoords[indexes[cI]];
      const center = getCenterOfTwoCoords(sCoord, rSCoord);
      const extendedEndCoord = getExtendedEndCoord(center, pivotCoord, rotation);
      await drawLineAnim(_lines[0], sCoord, rSCoord);
      await drawLineAnim(_lines[1], center, extendedEndCoord);
    }
    await drawPointsAnim([pivPoint]);
  }
  static async drawRotatedShape(lines: Line[], gridConfig: GridConfig, _rotatedShapeCoords: Coord[]): Promise<void> {
    const rotatedShapeCoords = _rotatedShapeCoords.map(c => GridArea.getGlobalCoord(gridConfig, c));
    for (let cI = 0; cI < rotatedShapeCoords.length; cI++) {
      const coord = rotatedShapeCoords[cI];
      const l = rotatedShapeCoords.length;
      const nextCoord = rotatedShapeCoords[cI + 1 >= l ? 0 : cI + 1];
      const line = lines[cI];

      await drawLineAnim(line, coord, nextCoord)
    }

    return await new Promise(res => res())
  }

  static removeAll(elements: (Line | Angle | PivotPoint)[]) {
    if (elements.length == 0) return;
    const parent = elements[0].parent;
    elements.forEach(l => {
      parent?.removeChild(l);
      l.destroy();
    })
  }
  static cancelHintAnim(elements: (Line | Angle | PivotPoint)[]) {
    rootEventBus.emit(EnumRootEventBus.cancelledCircleAnim);
    setTimeout(() => this.removeAll(elements), 10);
  }
}